import { useQuery } from '@tanstack/react-query';
import {
  Receipt,
  PaperPlaneTilt,
  Bell,
  Phone,
  Envelope,
  HandHeart,
  CurrencyGbp,
  CheckCircle,
  CircleNotch,
  Prohibit,
} from '@phosphor-icons/react';
import { fetchMatterPaymentStory, type MatterPaymentStory, type PaymentStoryEvent } from '@/lib/mattersApi';

interface Props {
  matterId: string;
}

const CHASE_LABEL: Record<string, string> = {
  email: 'Reminder sent',
  phone: 'Phone call',
  letter: 'Letter sent',
  in_person: 'Met in person',
};

const CHASE_ICON: Record<string, typeof Bell> = {
  email: Bell,
  phone: Phone,
  letter: Envelope,
  in_person: HandHeart,
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function describe(e: PaymentStoryEvent, money: (n: number) => string): {
  icon: typeof Receipt; accent: string; title: string; meta: string | null;
} {
  switch (e.type) {
    case 'raised':
      return { icon: Receipt, accent: 'text-brand-600 dark:text-brand-400', title: `Fee note ${e.feeNoteNumber} raised`, meta: e.amount != null ? money(e.amount) : null };
    case 'sent':
      return { icon: PaperPlaneTilt, accent: 'text-slate-500', title: 'Sent to solicitor', meta: e.feeNoteNumber ?? null };
    case 'chase': {
      const icon = (e.method && CHASE_ICON[e.method]) || Bell;
      const title = (e.method && CHASE_LABEL[e.method]) || 'Chased';
      return { icon, accent: 'text-amber-600 dark:text-amber-400', title, meta: [e.feeNoteNumber, e.by].filter(Boolean).join(' · ') || null };
    }
    case 'payment':
      if (e.writtenOff) {
        return { icon: Prohibit, accent: 'text-slate-500', title: 'Written off', meta: [e.amount != null ? money(e.amount) : null, e.allocatedTo?.join(', ')].filter(Boolean).join(' · ') || null };
      }
      return { icon: CurrencyGbp, accent: 'text-emerald-600 dark:text-emerald-400', title: 'Payment received', meta: [e.amount != null ? money(e.amount) : null, e.allocatedTo?.join(', ')].filter(Boolean).join(' · ') || null };
    case 'complete':
      return { icon: CheckCircle, accent: 'text-emerald-600 dark:text-emerald-400', title: 'Financially complete', meta: 'Ready to close' };
  }
}

export function MatterPaymentStory({ matterId }: Props) {
  const storyQ = useQuery<MatterPaymentStory>({
    queryKey: ['matter-payment-story', matterId],
    queryFn: () => fetchMatterPaymentStory(matterId),
    staleTime: 30_000,
  });

  const data = storyQ.data;
  const money = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: data?.currency ?? 'GBP', maximumFractionDigits: 2 }).format(n);

  if (storyQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CircleNotch size={13} className="animate-spin" />
        Reading the payment story…
      </div>
    );
  }
  if (!data || data.events.length === 0) {
    return <p className="text-xs text-muted-foreground">No financial activity on this matter yet.</p>;
  }

  return (
    <div>
      <h3 className="mb-3 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">Payment story</h3>
      <ol className="relative space-y-3">
        {data.events.map((e, i) => {
          const d = describe(e, money);
          const Icon = d.icon;
          const last = i === data.events.length - 1;
          return (
            <li key={e.id} className="relative flex gap-3">
              {/* Spine */}
              {!last && <span className="absolute left-[11px] top-6 h-[calc(100%-4px)] w-px bg-border" aria-hidden />}
              <span className={`relative z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-border bg-card ${d.accent}`}>
                <Icon size={13} weight={e.type === 'complete' ? 'fill' : 'duotone'} />
              </span>
              <div className="min-w-0 flex-1 pb-0.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className={`text-xs font-medium ${e.type === 'complete' ? 'text-emerald-700 dark:text-emerald-300' : 'text-foreground'}`}>{d.title}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{fmtDate(e.at)}</span>
                </div>
                {d.meta && <p className="mt-0.5 text-[11px] text-muted-foreground">{d.meta}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
